import { getCookie } from '@/lib/cookie';
import { showModal } from '@/lib/modal';
import type { EventOf } from '@/lib/utils';
import { $1, $, $e, csrfToken, on } from '@/lib/utils';

interface CurrentCartsItems {
  readonly items: number;
}

getCart();
on('change', 'form[data-cart-recalc] select[name^=quantity]', (ev: EventOf<HTMLSelectElement>) =>
  recalcCart(ev.currentTarget.form!),
);
on('click', 'form[data-cart-recalc] button[data-remove-item]', handleRemoveItem);
on('submit', 'form[data-gtag]', handleSubmitGtag);
on('submit', 'form.cart-add-item', handleCartAddItem);

on('change', '[data-change-refresh]', (ev: EventOf<HTMLSelectElement>) => {
  location.href = ev.currentTarget.value;
});

async function getCart(force = false) {
  let items = parseInt(getCookie('__Host-cart') || '', 10);
  if (force || Number.isNaN(items) || items < 0) {
    const res = await fetch('/ajax/current_carts_items');
    const json = (await res.json()) as CurrentCartsItems;
    items = json.items;
  }

  const elem = $('cart-quantities') as HTMLOutputElement;
  if (items > 0) Object.assign(elem, { hidden: false, value: items });
}

async function recalcCart(form: HTMLFormElement) {
  try {
    const res = await fetch(form.dataset.cartRecalc!, {
      method: 'PATCH',
      mode: 'same-origin',
      credentials: 'include',
      body: new FormData(form),
      headers: { 'X-CSRF-Token': csrfToken() },
      redirect: 'manual',
    });
    if (res.type === 'opaqueredirect' || res.status === 204) {
      location.reload();
    } else if (!res.ok) {
      alert(
        res.headers.get('Content-Type')?.includes('application/json')
          ? (await res.json()).error
          : '買い物かごの再計算に失敗しました。再読込します。',
      );
      location.reload();
    } else {
      form.querySelector('.cart-form-intern')!.innerHTML = await res.text();
    }
  } catch (err) {
    alert(err);
  }
}

function handleRemoveItem(ev: EventOf<HTMLButtonElement>) {
  const btn = ev.currentTarget;
  const form = btn.form!;
  if (form.dataset.cartDisallowEmpty && form.querySelectorAll('select[name^=quantity]').length <= 1) {
    alert('買い物かごの商品をすべて削除することはできません。代わりに注文を取り消してください。');
    return;
  }
  const quantity = btn.closest('tr')!.querySelector<HTMLSelectElement>('select[name^=quantity]');
  if (quantity && confirm(`『${btn.dataset.removeItem}』を買い物かごから削除します。`)) {
    const quantityValue = +quantity.value;
    quantity.replaceChildren($e('option', { value: '0' }, '0'));
    quantity.value = '0';
    recalcCart(form);
    try {
      const gtagItem = JSON.parse(btn.dataset.gtagItem!);
      gtag('event', 'remove_from_cart', {
        currency: 'JPY',
        value: gtagItem.price * quantityValue,
        items: [{ ...gtagItem, quantity: quantityValue }],
      });
    } catch (err) {
      console.warn(err);
    }
  }
}

function handleSubmitGtag(ev: EventOf<HTMLFormElement, SubmitEvent>) {
  const form = ev.currentTarget;
  if (!form.checkValidity()) return;
  try {
    const gtagData = JSON.parse(form.dataset.gtag!) as [string, object];
    gtag('event', ...gtagData);
  } catch (err) {
    console.warn(err);
  }
}

async function handleCartAddItem(ev: EventOf<HTMLFormElement, SubmitEvent>) {
  const form = ev.currentTarget;
  if (!form.checkValidity()) return;

  ev.preventDefault();

  const submitter = ev.submitter as HTMLButtonElement;
  const submitIcon = submitter.querySelector('i')!;
  submitIcon.classList.remove('fa-cart-plus');
  submitIcon.classList.add('fa-spinner');
  submitIcon.classList.add('fa-spin');
  submitter.disabled = true;
  const reset = () => {
    submitIcon.classList.remove('fa-spin');
    submitIcon.classList.remove('fa-spinner');
    submitIcon.classList.add('fa-cart-plus');
    submitter.disabled = false;
    form.classList.remove('is-modified');
    form.querySelectorAll('.is-modified').forEach((el) => el.classList.remove('is-modified'));
  };

  try {
    const formData = new FormData(form);
    const res = await fetch(form.action, {
      method: 'POST',
      mode: 'same-origin',
      credentials: 'include',
      body: formData,
      headers: { 'X-CSRF-Token': csrfToken() },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.error) throw new Error(json.error);

    try {
      const item = JSON.parse(form.dataset.gtagItem!);
      const quantity = parseInt(formData.get('quantity') as string, 10);
      gtag('event', 'add_to_cart', {
        currency: 'JPY',
        value: item.price * quantity,
        items: [{ ...item, quantity }],
      });
    } catch (err) {
      console.warn(err);
    }

    getCart(true).catch(console.error);
    if (!form.classList.contains('cart-add-item-inline')) {
      $1('#item-added .result')!.innerHTML = json.result;
      showModal($('item-added'));
      await new Promise((resolve) => setTimeout(resolve, 200));
    } else {
      form.querySelector('.inline-item-added')?.classList.remove('invisible');
      form.reset();
    }
  } catch (err) {
    alert(err);
  } finally {
    reset();
  }
}
